import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, User, Search, ArrowRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import { useSiteData } from '../lib/SiteDataContext';
import { useLang } from '../lib/LanguageContext';
import { resolveImage } from '../lib/api';

const CATEGORIES = ['all', 'presidential', 'government', 'education', 'infrastructure', 'investment'];

const NewsListPage = () => {
  const { news, settings } = useSiteData();
  const { t } = useLang();
  const header = settings?.page_headers?.news || {};
  const [params, setParams] = useSearchParams();
  const initial = params.get('cat') || 'all';
  const [active, setActive] = useState(initial);
  const [q, setQ] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = useMemo(() => {
    let list = (news || []).slice().sort((a, b) => a.order - b.order);
    if (active !== 'all') list = list.filter((n) => n.category === active);
    if (q.trim()) list = list.filter((n) => n.title.toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [active, q, news]);

  const setCat = (c) => {
    setActive(c);
    if (c === 'all') setParams({});
    else setParams({ cat: c });
  };

  return (
    <>
      <SEO
        title="News & Announcements"
        description="Latest news, announcements and official statements from the South East State of Somalia State House, ministries and government institutions."
        keywords="Somalia news, South East State news, Somali government announcements, Marka Cadeey news"
      />
      <PageHeader
        title={header.title || t('News & Announcements')}
        subtitle={header.subtitle}
        crumbs={[{ label: t('News') }]}
      />
      <section className="bg-[#FAF6EC] py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="mb-10 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium capitalize transition ${
                    active === c
                      ? 'border-[#C9A86A] bg-[#C9A86A] text-white'
                      : 'border-[#E6DDC8] bg-white text-[#0B2545] hover:border-[#C9A86A] hover:text-[#C9A86A]'
                  }`}
                >
                  {t(c === 'all' ? 'All' : c.charAt(0).toUpperCase() + c.slice(1))}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 rounded-md border border-[#E6DDC8] bg-white px-3 py-2 md:w-80">
              <Search size={15} className="text-[#0B2545]/60" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('Search news...')}
                className="w-full bg-transparent text-[13px] outline-none placeholder:text-[#0B2545]/50"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-lg border border-[#E6DDC8] bg-white p-12 text-center text-[#0B2545]/60">
              {t('No news found.')}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group overflow-hidden rounded-lg border border-[#E6DDC8] bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="relative h-[210px] w-full overflow-hidden bg-[#F6F2EA]">
                    {n.image_url && (
                      <img src={resolveImage(n.image_url)} alt={n.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    )}
                    <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1 text-[11.5px] font-medium text-[#0B2545]">
                      <Calendar size={12} className="text-[#C9A86A]" />
                      {n.date}
                      <span className="mx-1 h-3 w-px bg-[#0B2545]/20" />
                      <span className="uppercase tracking-wide text-[#C9A86A]">{t(n.category.charAt(0).toUpperCase() + n.category.slice(1))}</span>
                    </div>
                  </div>
                  <div className="flex flex-col p-6">
                    <h3 className="line-clamp-2 font-serif text-[17px] font-semibold leading-snug text-[#0B2545] group-hover:text-[#C9A86A]">{n.title}</h3>
                    <p className="mt-3 line-clamp-3 text-[13.5px] leading-relaxed text-[#0B2545]/70">{n.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between border-t border-[#E6DDC8] pt-4">
                      <div className="flex items-center gap-2 text-[12px] text-[#0B2545]/60">
                        <User size={13} className="text-[#C9A86A]" />
                        {n.author}
                      </div>
                      <span
                        data-testid="news-read-more"
                        className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#C9A86A] transition-colors group-hover:text-[#0B2545]"
                      >
                        {t('Read More')}
                        <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default NewsListPage;
